import create from 'zustand';
import {persist} from 'zustand/middleware';
import {
  setLocalStorage,
  getLocalStorage,
  removeLocalStorage,
} from '@/Utils/CommonUtils';

interface GamePlayState {
  showChallengeReject: boolean;
  showChallengeExpire: boolean;
  showChallengeCancel: boolean;
  showEndGameModal: boolean;
  showChangeInClient: boolean;
  tournamentPlay: boolean;
  playing: boolean;
  joinedTournament: boolean;
  joinedTournamentId: string;
  allowChallenge: boolean;
  _hasHydrated: boolean;
  // Rejected join_tournament_response (success: false), written by
  // SocketProvider's tournamentNavigation so a failure is captured even when
  // PlayChess's own joinTournamentResponseCallback isn't the (only) handler
  // registered for the event. `at` is a Date.now() stamp so a consumer can
  // ignore anything written before it mounted (no stale-failure replay on
  // remount/cold start). null once consumed/acked.
  // `conflict` is optional: it is only populated once the server sends
  // conflict_id (OVERLAPPING_TOURNAMENT rollout). This value is persisted, so
  // a write from an older build (or an older server) will lack `conflict` —
  // every consumer must treat it as optional, not assume its presence.
  joinFailure: {
    message: string;
    at: number;
    conflict?: {id: number; name?: string};
  } | null;
}

interface GamePlayActions {
  setShowChallengeReject: (val: boolean) => void;
  setShowChallengeExpire: (val: boolean) => void;
  setShowChallengeCancel: (val: boolean) => void;
  setShowEndGameModal: (val: boolean) => void;
  setShowChangeInClient: (val: boolean) => void;
  setTournamentPlay: (val: boolean) => void;
  setPlaying: (val: boolean) => void;
  setJoinedTournament: (val: boolean) => void;
  setJoinedTournamentId: (val: string) => void;
  setAllowChallenge: (val: boolean) => void;
  initializeAllowChallenge: () => Promise<void>;
  setHasHydrated: (val: boolean) => void;
  setJoinFailure: (
    val: {
      message: string;
      at: number;
      conflict?: {id: number; name?: string};
    } | null,
  ) => void;
}

const useGamePlayState = create(
  persist<GamePlayState & GamePlayActions>(
    (set, get) => ({
      // Default state
      showChallengeReject: false,
      showChallengeExpire: false,
      showChallengeCancel: false,
      showEndGameModal: false,
      showChangeInClient: false,
      tournamentPlay: false,
      playing: false,
      joinedTournament: false,
      joinedTournamentId: '0',
      allowChallenge: true,
      _hasHydrated: false,
      joinFailure: null,

      // Actions
      setShowChallengeReject: val => set({showChallengeReject: val}),
      setShowChallengeExpire: val => set({showChallengeExpire: val}),
      setShowChallengeCancel: val => set({showChallengeCancel: val}),
      setShowEndGameModal: val => set({showEndGameModal: val}),
      setShowChangeInClient: val => set({showChangeInClient: val}),
      setTournamentPlay: val => set({tournamentPlay: val}),
      setPlaying: val => set({playing: val}),
      setJoinedTournament: val => set({joinedTournament: val}),
      setJoinedTournamentId: val => set({joinedTournamentId: val}),
      setAllowChallenge: val => set({allowChallenge: val}),
      setHasHydrated: val => set({_hasHydrated: val}),
      setJoinFailure: val => set({joinFailure: val}),

      initializeAllowChallenge: async () => {
        try {
          const dndMode = await getLocalStorage('dnd-mode');
          console.log('DND Mode from storage:', dndMode);

          if (dndMode === 'nobody') {
            set({allowChallenge: false});
          } else if (dndMode === 'everyone') {
            set({allowChallenge: true});
          }
        } catch (err) {
          console.warn('Error initializing allowChallenge from storage', err);
        }
      },
    }),
    {
      name: 'game-play-state',
      getStorage: () => ({
        getItem: async name => {
          const result = await getLocalStorage(name);
          return result ? JSON.parse(result) : null;
        },
        setItem: async (name, value) => {
          await setLocalStorage(name, JSON.stringify(value));
        },
        removeItem: async name => {
          await removeLocalStorage(name);
        },
      }),
      onRehydrateStorage: () => state => {
        state?.setHasHydrated(true);
        // Initialize after hydration completes
        state?.initializeAllowChallenge();
      },
    },
  ),
);

export default useGamePlayState;
